import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, X } from 'lucide-react';
import { losApi } from './losApi';
import { ICON_MAP, timeAgo } from './NotificationBell';

// "View all" target for the top-nav bell: the newest 100 (the server's cap).
const PAGE_LIMIT = 100;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await losApi.listNotifications(unreadOnly, PAGE_LIMIT);
      setItems(res.items || []);
      setUnread(res.unread_count || 0);
    } catch { /* ignore silently, likely 401 during logout */ }
    setLoading(false);
  }, [unreadOnly]);

  useEffect(() => { load(); }, [load]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await losApi.markNotificationRead(n.notification_id); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.link) navigate(n.link);
  };

  const dismiss = async (n) => {
    try { await losApi.dismissNotification(n.notification_id); } catch { /* ignore */ }
    setItems((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
  };

  const markAll = async () => {
    try { await losApi.markAllNotificationsRead(); } catch { /* ignore */ }
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="notifications-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: '#070B5F' }}>Notifications</h1>
          <div className="text-[12px] text-slate-500">{unread > 0 ? `${unread} unread` : 'All caught up'}</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[12px] text-slate-600 inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
              data-testid="notifications-unread-only"
            />
            Unread only
          </label>
          {unread > 0 && (
            <button
              onClick={markAll}
              data-testid="notifications-mark-all-read"
              className="text-[12px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-50"
              style={{ color: '#070B5F' }}
            >
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && items.length === 0 && (
          <div className="p-8 text-center text-[12px] text-slate-400">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-[13px] text-slate-500" data-testid="notifications-empty">
            {unreadOnly ? 'Nothing unread.' : 'No notifications yet.'}
          </div>
        )}
        {items.map((n) => {
          const Icon = ICON_MAP[n.icon] || Bell;
          return (
            <div
              key={n.notification_id}
              className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
              style={n.read ? {} : { background: 'rgba(107,168,46,0.04)' }}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(7,11,95,0.06)' }}>
                <Icon size={14} style={{ color: '#070B5F' }} />
              </div>
              <button
                onClick={() => openItem(n)}
                data-testid={`notifications-item-${n.notification_id}`}
                className="flex-1 min-w-0 text-left"
              >
                <div className="text-[13px] leading-tight" style={{ color: '#070B5F', fontWeight: n.read ? 500 : 700 }}>{n.title}</div>
                {n.description && <div className="text-[12px] text-slate-500 mt-0.5">{n.description}</div>}
                <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-semibold">{timeAgo(n.created_at)} ago</div>
              </button>
              <button
                onClick={() => dismiss(n)}
                data-testid={`notifications-dismiss-${n.notification_id}`}
                className="text-slate-300 hover:text-slate-600 p-0.5"
                title="Dismiss"
              >
                <X size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
